/**
 * AddButton — shared `[+ add …]` affordance for the VQE sections (PRD § 1.4
 * hand-off contract). Edit-ready callers pass `disabled={false}` and a live
 * handler; without an edit-ready context it renders its disabled scaffold.
 *
 * Disabled-state copy is shown via a shadcn `VqeTooltip` with `selfProvider` so
 * the component stays renderable outside of the app-root `<TooltipProvider>`
 * (e.g., in unit tests).
 */

import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from './VqeTooltip';

export interface AddButtonProps {
  readonly label: string;
  readonly testId: string;
  readonly disabled?: boolean;
  readonly tooltip?: string;
  readonly onClick?: () => void;
}

const DEFAULT_TOOLTIP = vqeCopy.disabled.editingUnavailable;

export function AddButton({
  label,
  testId,
  disabled = true,
  tooltip = DEFAULT_TOOLTIP,
  onClick,
}: AddButtonProps) {
  return (
    <VqeTooltip content={disabled ? tooltip : undefined} selfProvider>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 gap-1 px-2 text-xs text-muted-foreground"
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        data-testid={testId}
        aria-disabled={disabled}
      >
        <Plus className="h-3.5 w-3.5" />
        {label}
      </Button>
    </VqeTooltip>
  );
}
